// src/api/entitledImagery.js
//
// GET /imagery/entitled (authz-api, footprint restyle S-a): {mask, features},
// the map's entitled mask as /tile/session decides it, and (S-e5) `reference`,
// Sentinel-2's entitled areas, which the strips read for coverage and nothing
// draws. An answer without it (an authz-api before S-e5) reads as none. include=<dataset_id>
// adds one dataset the default leaves out (a synthetic capture a SitePlanner
// page has pinned or picked); it is re-checked against the grants like any other.
// with_reference=<dataset_id> (S-c4) opens one Sentinel-2 dataset's entitled
// area in the mask, for a map that shows that composite on top; a dataset the
// org may not open is a 403 REFERENCE_NOT_ENTITLED, thrown as a ReferenceRefused
// so the hook can ask again without it (useEntitledFootprints).
//
// The browser's HTTP cache does the ETag work: the route answers
// `Cache-Control: private, max-age=30` with an ETag, so a repeat within 30 s
// is served from cache and a later one is revalidated (304) without a body.
//
// Any answer that is not a 200 with a polygon mask and a features array is an
// error: the caller keeps the world masked (useEntitledFootprints).
import { useAuthStore } from "../stores/useAuthStore";

const MASK_TYPES = new Set(["Polygon", "MultiPolygon"]);

export const REFERENCE_NOT_ENTITLED = "REFERENCE_NOT_ENTITLED";

/** The route refused with_reference: 403 REFERENCE_NOT_ENTITLED. */
export class ReferenceRefused extends Error {
  constructor(datasetId) {
    super(`entitled imagery: 403 ${REFERENCE_NOT_ENTITLED}`);
    this.code = REFERENCE_NOT_ENTITLED;
    this.datasetId = datasetId;
  }
}

export function entitledUrl(include = null, withReference = null) {
  const q = [];
  if (include) q.push(`include=${encodeURIComponent(include)}`);
  if (withReference) q.push(`with_reference=${encodeURIComponent(withReference)}`);
  return q.length ? `/imagery/entitled?${q.join("&")}` : "/imagery/entitled";
}

/** {mask, features, reference}, or a throw (a ReferenceRefused for a refused with_reference). */
export async function fetchEntitled({ include = null, withReference = null, signal } = {}) {
  const token = await useAuthStore.getState().ensureFreshToken();
  if (!token) throw new Error("no_auth_token");
  const res = await fetch(entitledUrl(include, withReference), { headers: { Authorization: `Bearer ${token}` }, signal });
  if (res.status === 403 && withReference) {
    const err = await res.json().catch(() => null);
    if (err?.error === REFERENCE_NOT_ENTITLED) throw new ReferenceRefused(withReference);
  }
  if (!res.ok) throw new Error(`entitled imagery: ${res.status}`);
  const body = await res.json();
  if (!MASK_TYPES.has(body?.mask?.type) || !Array.isArray(body?.features)) throw new Error("entitled imagery: no mask in the answer");
  return { mask: body.mask, features: body.features, reference: Array.isArray(body.reference) ? body.reference : [] };
}
